import type { LocationDescriptorObject } from 'history';
import moment from 'moment';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import user from 'js/lib/user';

import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import type { DiscretionaryDiscount } from 'bundles/naptimejs/resources/enterpriseDiscretionaryDiscounts.v1';
import { ANNUALLY, QUARTERLY, UNLIMITED, billingTypes } from 'bundles/teams-v2/constants/billing';
import { FREE_TRIAL_LICENSES } from 'bundles/teams-v2/constants/freeTrial';
import type {
  EnterpriseLeadOrganizationSizeCategory,
  EnterpriseLeadOrganizationType,
  EnterpriseLeadSource,
  UserData,
} from 'bundles/teams-v2/utils/enterpriseLeadUtils';
import {
  createProspectiveEnterpriseLead,
  enterpriseLeadTransactionType,
} from 'bundles/teams-v2/utils/enterpriseLeadUtils';

export const getQueryFromRouter = (router: InjectedRouter, queryKey: string): string => {
  if (queryKey === 'learnerCount') {
    throw new Error(
      'It is unsafe to retrieve learnerCount through this utility function, use getLearnerCountFromRouter.'
    );
  }

  return router?.location?.query?.[queryKey];
};

export const getQueryFromLocation = (location: LocationDescriptorObject, queryKey: string): string | undefined => {
  if (queryKey === 'learnerCount') {
    throw new Error(
      'It is unsafe to retrieve learnerCount through this utility function, use getLearnerCountFromRouter.'
    );
  }

  return location?.query?.[queryKey];
};

export const updateRouterParam = (
  router: InjectedRouter,
  pathname: string,
  updateQuery: Record<string, string | number>
) => {
  const query = router.location?.query ?? {};
  router.replace({
    pathname,
    query: { ...query, ...updateQuery },
  });
};

export const getLearnerCountFromRouter = (router: InjectedRouter, discretionaryDiscount?: DiscretionaryDiscount) => {
  const queryKey = 'learnerCount';
  const isFreeTrialEnabled = EnterpriseExperiments.get('enableFreeTrial');
  const freeTrial = router?.location?.query?.freetrial === '1';

  if (isFreeTrialEnabled && freeTrial) {
    return FREE_TRIAL_LICENSES;
  }

  // Prevents URL manipulation
  if (discretionaryDiscount) {
    return discretionaryDiscount.licenseCount;
  }

  return (router && router.location && router.location.query && router.location.query[queryKey]) || null;
};

export const getBillingType = (billingType: number) => {
  switch (billingType) {
    case ANNUALLY:
    default:
      return billingTypes.ONE_TIME;
    case QUARTERLY:
      return billingTypes.QUARTERLY;
  }
};

export const createEnterpriseLeadForStep = (
  router: InjectedRouter,
  fullName: string,
  businessEmail: string,
  isFreeTrial: boolean,
  source: EnterpriseLeadSource
) => {
  const userData: UserData = {
    email_address: businessEmail /* eslint-disable-line camelcase */,
    full_name: fullName /* eslint-disable-line camelcase */,
  };

  if (user.isAuthenticatedUser()) {
    userData.id = user.get().id;
  }

  const organizationName = getQueryFromRouter(router, 'organizationName') || '';
  const organizationType = getQueryFromRouter(router, 'organizationType') as EnterpriseLeadOrganizationType;
  const organizationSizeCategory = getQueryFromRouter(
    router,
    'organizationSizeCategory'
  ) as EnterpriseLeadOrganizationSizeCategory;
  const role = getQueryFromRouter(router, 'role') || '';
  const userLimit = parseInt(getLearnerCountFromRouter(router), 10) || 0;
  const billingType = getBillingType(parseInt(getQueryFromRouter(router, 'billingType'), 10));
  const transactionType = isFreeTrial
    ? enterpriseLeadTransactionType.FREE_TRIAL_CHECKOUT_STARTED
    : enterpriseLeadTransactionType.STANDARD_CHECKOUT_STARTED;
  createProspectiveEnterpriseLead({
    userData,
    organizationName,
    organizationType,
    organizationSizeCategory,
    userLimit,
    coursesPerUserLimit: UNLIMITED,
    billingType,
    role,
    source,
    transactionType,
  });
};

export const isDiscretionaryDiscountIncorrectUser = ({ purchaserUserId }: DiscretionaryDiscount) => {
  return user.get()?.id !== purchaserUserId;
};

export const isDiscretionaryDiscountAppled = ({ appliedAt }: DiscretionaryDiscount) => {
  return !!appliedAt;
};

export const isDiscretionaryDiscountExpired = ({ expiresAt }: DiscretionaryDiscount) => {
  return expiresAt && Date.now() > expiresAt;
};

export const getValidDiscretionaryDiscount = (discretionaryDiscount: DiscretionaryDiscount | undefined) => {
  /**
   * Exclude:
   *   Incorrect user
   *   Expired
   *   Already purchased
   */
  if (discretionaryDiscount === undefined) {
    return undefined;
  }

  const isIncorrectUser = isDiscretionaryDiscountIncorrectUser(discretionaryDiscount);
  const isExpired = isDiscretionaryDiscountExpired(discretionaryDiscount);
  const isApplied = isDiscretionaryDiscountAppled(discretionaryDiscount);

  return !isIncorrectUser && !isExpired && !isApplied ? discretionaryDiscount : undefined;
};

export const isCheckingOutForExistingOrg = (router: InjectedRouter) => {
  const thirdPartyOrganizationId = getQueryFromRouter(router, 'thirdPartyOrganizationId');
  return user.isAuthenticatedUser() && thirdPartyOrganizationId;
};

export const planAutoRenewDate = moment().add(1, 'year').calendar();

// Free trial start date = (now@UTC + 15 days).round to start of day
export const calculateFreeTrialBillingStartDate = () => moment(Date.now()).utc().add(15, 'days').startOf('day').local();
